// The in-call chat: messages with tappable links and a Copy button for each,
// and a box to write in. Made for dropping links, works as a plain chat.

import { useEffect, useLayoutEffect, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { chatSegments, type ChatMessage } from "@breeze/core";
import type { ChatStore } from "../hooks/useChat";
import { TrashIcon } from "./icons";

interface ChatPanelProps {
  readonly chat: ChatStore;
  readonly selfId: string | null;
  readonly isHost: boolean;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    area.remove();
    return ok;
  }
}

function CopyButton({ href, label }: { readonly href: string; readonly label: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return (
    <button
      type="button"
      className="chat__copy"
      onClick={() => {
        void copyText(href).then((ok) => {
          if (!ok) return;
          setDone(true);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => setDone(false), 1600);
        });
      }}
    >
      {done ? "Copied" : label}
    </button>
  );
}

function hostOf(href: string): string {
  return href.replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0] ?? href;
}

function MessageRow({
  message,
  mine,
  canDelete,
  onDelete,
}: {
  readonly message: ChatMessage;
  readonly mine: boolean;
  readonly canDelete: boolean;
  readonly onDelete: () => void;
}): JSX.Element {
  const segments = chatSegments(message.text);
  const links = segments.filter((s): s is Extract<typeof s, { kind: "link" }> => s.kind === "link");
  const seen = new Set<string>();
  const unique = links.filter((l) => !seen.has(l.href) && seen.add(l.href));
  return (
    <li className={`chat__msg${mine ? " chat__msg--mine" : ""}`}>
      <div className="chat__meta">
        <span className="chat__who">{mine ? "You" : message.authorName || "Guest"}</span>
        <time className="chat__time" dateTime={new Date(message.at).toISOString()}>
          {new Date(message.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
        </time>
        {canDelete ? (
          <button type="button" className="chat__del" aria-label="Delete message" onClick={onDelete}>
            <TrashIcon size={14} />
          </button>
        ) : null}
      </div>
      <p className="chat__text">
        {segments.map((s, i) =>
          s.kind === "link" ? (
            <a key={i} href={s.href} target="_blank" rel="noopener noreferrer nofollow">
              {s.text}
            </a>
          ) : (
            <span key={i}>{s.text}</span>
          ),
        )}
      </p>
      {unique.length > 0 ? (
        <div className="chat__links">
          {unique.map((l) => (
            <CopyButton key={l.href} href={l.href} label={unique.length > 1 ? `Copy ${hostOf(l.href)}` : "Copy link"} />
          ))}
        </div>
      ) : null}
    </li>
  );
}

/** Tallest the message box grows before it scrolls, in px (matches .chat__box). */
const MAX_BOX = 120;

export function ChatPanel({ chat, selfId, isHost }: ChatPanelProps): JSX.Element {
  const { messages, send, remove, markRead, problem } = chat;
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLUListElement>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const stick = useRef(true);

  // Open at the newest message, and stay there unless he scrolled up to read.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
    markRead();
  }, [messages, markRead]);

  const fit = (): void => {
    const el = boxRef.current;
    if (!el) return;
    el.style.height = "auto";
    // scrollHeight leaves out the border, and the box is border-box, so without
    // the border the content is 2px too tall for it and a scrollbar appears.
    const want = el.scrollHeight + (el.offsetHeight - el.clientHeight);
    el.style.height = `${Math.min(want, MAX_BOX)}px`;
    el.style.overflowY = want > MAX_BOX ? "auto" : "hidden";
  };

  const submit = (): void => {
    if (!draft.trim()) return;
    send(draft);
    setDraft("");
    stick.current = true;
    requestAnimationFrame(() => {
      fit();
      boxRef.current?.focus();
    });
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="chat">
      <ul
        ref={listRef}
        className="chat__list"
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        }}
      >
        {messages.length === 0 ? <li className="chat__empty">No messages yet.</li> : null}
        {messages.map((m) => (
          <MessageRow
            key={m.id}
            message={m}
            mine={m.authorId === selfId}
            canDelete={isHost || m.authorId === selfId}
            onDelete={() => remove(m.id)}
          />
        ))}
      </ul>
      {problem ? (
        <p className="chat__problem" role="status">
          {problem}
        </p>
      ) : null}
      <div className="chat__compose">
        <textarea
          ref={boxRef}
          className="chat__box"
          rows={1}
          value={draft}
          placeholder="Message"
          aria-label="Message"
          enterKeyHint="send"
          autoComplete="off"
          autoCorrect="on"
          onChange={(e) => {
            setDraft(e.target.value);
            fit();
          }}
          onKeyDown={onKey}
        />
        <button type="button" className="chat__send" disabled={!draft.trim()} onClick={submit}>
          Send
        </button>
      </div>
    </div>
  );
}
